// Custom Kendo Error Handler (ESM version)
// Provides exported functions plus an optional global shim for legacy Razor inline usage.

let popupNotification = null;
let notificationSelector = '#MainErrorPopupNotification';

function ensurePopup() {
    if (!popupNotification) {
        const el = window.jQuery ? window.jQuery(notificationSelector) : null;
        if (el && el.length) {
            popupNotification = el.data('kendoNotification');
        }
    }
    return popupNotification;
}

export function configure({ selector } = {}) {
    if (selector) {
        notificationSelector = selector;
        popupNotification = null; // force re-resolve on next use
    }
}

export function initNotifications() {
    ensurePopup();
}

export function showPopup(message, type = 'info') {
    const popup = ensurePopup();
    if (!popup) return; // fail-soft if not ready
    popup.show(message, type);
}

export function showPopupForErrors(errors) {
    if (!errors) return;
    const popup = ensurePopup();
    if (!popup) return;
    window.jQuery.each(errors, function (_key, value) {
        if ('errors' in value) {
            window.jQuery.each(value.errors, function () {
                popup.show(this, 'error');
            });
        }
    });
}

export function gridCrudSuccessMsg(onRequestEndEvent, entityName) {
    if (!onRequestEndEvent || onRequestEndEvent.response?.Errors) return;
    switch (onRequestEndEvent.type) {
        case 'update':
            showPopup(`${entityName} successfully updated.`, 'success');
            break;
        case 'create':
            showPopup(`${entityName} successfully created.`, 'success');
            break;
        case 'destroy':
            showPopup(`${entityName} successfully deleted.`, 'success');
            break;
    }
}

export function gridEditorValidationSummary(errors, gridName) {
    if (!errors || !gridName) return;
    const $ = window.jQuery;
    const grid = $("#" + gridName).data('kendoGrid');
    if (!grid) return;
    grid.one('dataBinding', function (e) {
        e.preventDefault();
        window.jQuery.each(errors, function (_key, value) {
            let message = '';
            if ('errors' in value) {
                window.jQuery.each(value.errors, function () {
                    message += `<li>${this}</li>`;
                });
            }
            grid.editable.element
                .find('[data-valmsg-summary="true"]')
                .html(`<ul>${message}</ul>`);
        });
    });
}

export function popupWindowValidationSummary(errors, kendoWindowInstance) {
    if (!errors || !kendoWindowInstance) return;
    window.jQuery.each(errors, function (_key, value) {
        let message = '';
        if ('errors' in value) {
            window.jQuery.each(value.errors, function () {
                message += `<li>${this}</li>`;
            });
        }
        kendoWindowInstance.element
            .find('[data-valmsg-summary="true"]')
            .html(`<ul>${message}</ul>`);
    });
}

/**
 * Builds the login redirect URL for the current tenant, preserving the current page as the ReturnUrl.
 */
function getLoginRedirectUrl() {
    const tenant = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    return tenant ? `/${tenant}/Identity/Account/Login?ReturnUrl=${returnUrl}` : `/Identity/Account/Login?ReturnUrl=${returnUrl}`;
}

/**
 * Installs global interceptors that redirect to the login page when any
 * API request responds with 401 (session expired / not authenticated).
 * Safe to call multiple times — installs only once.
 */
export function initSessionExpiryHandling() {
    if (window.__sessionExpiryHandlerInstalled) return;
    window.__sessionExpiryHandlerInstalled = true;

    // Intercept native fetch
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);
        if (response.status === 401) {
            window.location.href = getLoginRedirectUrl();
        }
        return response;
    };

    // Intercept jQuery AJAX (used by Kendo DataSource)
    if (window.jQuery) {
        window.jQuery(document).ajaxError(function (_event, jqXHR) {
            if (jqXHR.status === 401) {
                window.location.href = getLoginRedirectUrl();
            }
        });
    }
}

/**
 * Attaches a 401 error listener to a MapLibre map instance.
 * MapLibre captures window.fetch at module init time (before our wrapper installs),
 * so tile requests that return 401 must be caught via the map's own error event instead.
 */
export function registerMap(map) {
    if (!map || typeof map.on !== 'function') return;
    map.on('error', (e) => {
        if (e?.error?.status === 401) {
            window.location.href = getLoginRedirectUrl();
        }
    });
}

// Backward-compatible global shim
(function attachGlobal() {
    if (typeof window === 'undefined') return;
    if (!window.CustomKendoErrorHandler) {
        window.CustomKendoErrorHandler = {
            configure,
            initNotifications,
            showPopup,
            showPopupForErrors,
            GridCrudSuccessMsg: gridCrudSuccessMsg,
            GridEditorValidationSummary: gridEditorValidationSummary,
            popupWindowValidationSummary,
            initSessionExpiryHandling,
            registerMap,
        };
    }
})();
